import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ContextMenu from "~/images/context_menu.svg?react";
import UserIcon from "~/images/user.svg?react";
import type { Knowledge } from "~/interface/knowledge";
import KnowledgeStatusBadge from "./KnowledgeStatusBadge";
import styles from "./KnowledgeTable.module.scss";

interface KnowledgeTableData {
  title: string;
  prompt: string;
  author: string;
  date: string;
  enabled: boolean;
  type: Knowledge["type"];
}

interface Props {
  knowledges?: Knowledge[] | undefined;
  allKnowledges?: Knowledge[] | undefined;
  onToggleActive?: ((knowledge: Knowledge) => void) | undefined;
  onDelete?: ((knowledge: Knowledge) => void) | undefined;
  onEdit?: ((knowledge: Knowledge) => void) | undefined;
  searchQuery?: string | undefined;
}

// Unixタイムスタンプを日付に変換するユーティリティ
const parseTimestamp = (timestamp: string | number): Date | null => {
  try {
    // 文字列の場合は数値に変換
    const numericTimestamp = typeof timestamp === "string" ? parseFloat(timestamp) : timestamp;

    // Unixタイムスタンプ（秒単位）の場合
    if (numericTimestamp > 1000000000 && numericTimestamp < 10000000000) {
      return new Date(numericTimestamp * 1000);
    }

    // ミリ秒単位のタイムスタンプの場合
    if (numericTimestamp > 1000000000000) {
      return new Date(numericTimestamp);
    }

    // ISO文字列として試行
    const date = new Date(timestamp);
    if (!Number.isNaN(date.getTime())) {
      return date;
    }

    return null;
  } catch (error) {
    console.error("❌ Error parsing timestamp:", error);
    return null;
  }
};

// KnowledgeをKnowledgeTableData形式に変換
const mapKnowledgeToKnowledge = (knowledge: Knowledge): KnowledgeTableData => {
  const formatDate = (dateString: string) => {
    // まずUnixタイムスタンプとして解析を試行
    const parsedDate = parseTimestamp(dateString);
    if (parsedDate) {
      const month = parsedDate.getMonth() + 1;
      const day = parsedDate.getDate();
      return `${month}/${day}`;
    }

    // フォールバック: 従来の方法
    try {
      const date = new Date(dateString);
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${month}/${day}`;
    } catch (error) {
      console.error("❌ Error formatting date:", error);
      return "不明";
    }
  };

  // knowledgeがundefinedやnullの場合の安全な処理
  const safeKnowledge = knowledge.meta_prompt || "";
  const truncatedPrompt =
    safeKnowledge.length > 80 ? `${safeKnowledge.substring(0, 80)}…` : safeKnowledge;

  return {
    title: knowledge.title || "無題",
    prompt: truncatedPrompt,
    author: knowledge.modified_by || "不明", // 作成者情報がない場合のフォールバック
    date: formatDate(knowledge.created_at),
    enabled: knowledge.isActive,
    type: knowledge.type,
  };
};

// テキストをハイライト表示するヘルパー関数
const highlightText = (text: string, query: string): React.ReactNode => {
  if (!query || !text) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);

  if (index === -1) return text;

  return (
    <>
      {text.substring(0, index)}
      <mark style={{ backgroundColor: "#fef08a", padding: "2px 0", borderRadius: "2px" }}>
        {text.substring(index, index + query.length)}
      </mark>
      {text.substring(index + query.length)}
    </>
  );
};

const KnowledgeTable: React.FC<Props> = ({
  knowledges = [],
  allKnowledges: _allKnowledges = [],
  onDelete,
  onEdit,
  onToggleActive,
  searchQuery = "",
}) => {
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const [togglingIndex, setTogglingIndex] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { organizationId, projectId } = useParams();

  console.log("📊 KnowledgeTable received data:", knowledges);

  // 有効化されているものを優先的に上に表示するためのソート
  const sortedKnowledges = [...knowledges].sort((a, b) => {
    // 有効化されているものを優先
    if (a.isActive !== b.isActive) {
      return b.isActive ? 1 : -1;
    }
    // 次に作成日でソート
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  // Knowledgeデータを表示用に変換
  const rows = sortedKnowledges.map(mapKnowledgeToKnowledge);
  console.log("🔄 Converted API data for display:", rows);

  // メニュー外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleContextMenuClick = (index: number) => {
    console.log("📋 Context menu clicked:", { index });
    setOpenMenuIndex(openMenuIndex === index ? null : index);
  };

  const handleEdit = (index: number) => {
    console.log("✏️ Edit clicked:", {
      index,
      totalRows: rows.length,
    });

    if (onEdit && sortedKnowledges[index]) {
      const knowledge = sortedKnowledges[index]!;
      console.log("✏️ Editing knowledge:", knowledge);
      onEdit(knowledge);
    } else {
      console.log("⚠️ Edit ignored - no data or handler");
    }
    setOpenMenuIndex(null); // メニューを閉じる
  };

  const handleDelete = (index: number) => {
    console.log("🗑️ Delete clicked:", {
      index,
      totalRows: rows.length,
    });

    if (onDelete && sortedKnowledges[index]) {
      const knowledge = sortedKnowledges[index]!;
      console.log("🗑️ Deleting knowledge:", knowledge);
      onDelete(knowledge);
    } else {
      console.log("⚠️ Delete ignored - no data or handler");
    }
    setOpenMenuIndex(null); // メニューを閉じる
  };

  const handleRowClick = (index: number) => {
    if (sortedKnowledges[index] && organizationId && projectId) {
      const knowledgeId = sortedKnowledges[index]!.id;
      console.log("🔗 Navigating to knowledge detail:", {
        organizationId,
        projectId,
        knowledgeId,
      });
      navigate(`/${organizationId}/project/${projectId}/knowledge/${knowledgeId}`);
    }
  };

  // 【状態切り替えハンドラ】: ナレッジの有効/無効を切り替え
  // 【実装方針】: 親コンポーネントのonToggleActiveを呼び出す
  const handleToggleStatus = async (index: number) => {
    if (onToggleActive && sortedKnowledges[index]) {
      const knowledge = sortedKnowledges[index]!;
      console.log("🔄 Toggling knowledge status:", { index, knowledge });
      setTogglingIndex(index);
      try {
        await onToggleActive(knowledge);
      } finally {
        setTogglingIndex(null);
      }
    }
  };

  return (
    <div className={styles["tableWrapper"]} data-testid="knowledge-list">
      <table className={styles["knowledgeTable"]}>
        <thead>
          <tr>
            <th>タイトル</th>
            <th>プロンプト</th>
            <th>作成者</th>
            <th>作成日</th>
            <th>状態</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map((row: KnowledgeTableData, idx: number) => (
              <tr
                key={sortedKnowledges[idx]?.id || idx}
                className={styles["clickableRow"]}
                onClick={() => handleRowClick(idx)}
                data-testid="knowledge-item"
                data-knowledge-id={sortedKnowledges[idx]?.id}
              >
                <td className={styles["titleCell"]}>
                  <div className={styles["textWithEllipsis"]} title={row.title}>
                    {highlightText(row.title, searchQuery)}
                  </div>
                </td>
                <td>
                  <div className={styles["textWithEllipsis"]} title={row.prompt}>
                    {highlightText(row.prompt, searchQuery)}
                  </div>
                </td>
                <td className={styles["authorCell"]}>
                  <div className={styles["authorWrap"]}>
                    <span className={styles["authorName"]} title={row.author}>
                      <UserIcon className={styles["icon-user"]} />
                      {row.author}
                    </span>
                  </div>
                </td>
                <td>{row.date}</td>
                <td className={styles["statusCell"]} onClick={(e) => e.stopPropagation()}>
                  <KnowledgeStatusBadge
                    isActive={row.enabled}
                    onToggle={onToggleActive ? () => handleToggleStatus(idx) : undefined}
                    disabled={togglingIndex === idx}
                  />
                </td>
                {onEdit && onDelete && (
                  <td className={styles["contextMenuCell"]} onClick={(e) => e.stopPropagation()}>
                    <div
                      className={styles["contextMenuContainer"]}
                      ref={openMenuIndex === idx ? menuRef : null}
                    >
                      <ContextMenu
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleContextMenuClick(idx);
                        }}
                        data-testid="knowledge-context-trigger"
                      />
                      {openMenuIndex === idx && (
                        <div
                          className={styles["contextMenuDropdown"]}
                          data-testid="knowledge-context-menu"
                        >
                          <div
                            className={styles["menuItem"]}
                            onClick={() => handleEdit(idx)}
                            data-testid="knowledge-edit"
                          >
                            編集
                          </div>
                          <div
                            className={styles["menuItem"]}
                            onClick={() => handleDelete(idx)}
                            data-testid="knowledge-delete"
                          >
                            削除
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={7} style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                ナレッジがありません
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default KnowledgeTable;
